import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowUpRight, ArrowRight, Check, Sparkles } from "lucide-react";
import KoreCoachedContent from "./KoreCoachedContent";
import Seo from "@/components/Seo";
import { trackCta } from "@/lib/analytics";

// Introductory offers per membership, aligned with the Home pathway promises.
const INTRO_OFFERS = {
  "kore-coached": {
    tag: "How most members start",
    heading: "Start your 30 Day Trial · £99",
    body: "Experience the full KORE Coaching environment for 30 days. Two coached KORE Strength sessions each week (Monday & Wednesday or Tuesday & Thursday) and coach support — before you commit to monthly.",
    ctaLabel: "Start your 30 Day Trial",
    ctaTo: "/30-day-trial",
  },
};

// Access has a rolling-monthly and a 12-month price. Coached is a single rolling rate.
const PRICING = {
  "kore-access": [
    { label: "12-month membership", price: 39, sub: "per month", note: "Best value" },
    { label: "Rolling monthly", price: 49, sub: "per month", note: "No commitment" },
  ],
  "kore-coached": [
    { label: "Rolling monthly membership", price: 149, sub: "per month", note: "Start with the 30 Day Coached Trial" },
  ],
};

export default function MembershipDetail() {
  const { slug } = useParams();
  const [m, setM] = useState(null);
  useEffect(() => { api.get(`/memberships/${slug}`).then((r) => setM(r.data)).catch(() => setM(null)); }, [slug]);
  if (!m) return <div className="container-kore py-40 text-kore-ink/50">Loading…</div>;

  const offer = INTRO_OFFERS[slug];
  const pricing = PRICING[slug] || [{ label: `${m.name} membership`, price: m.price_gbp, sub: `per ${m.interval}` }];
  const isAccess = slug === "kore-access";

  const primaryTitle = isAccess
    ? "Kore Access — from £39 / month"
    : "Kore Coaching — £149 / month";
  const primaryDesc = isAccess
    ? "Calm, welcoming gym access in Bowness-on-Windermere without the intimidating big-gym atmosphere. £39/mo on 12-month, £49/mo rolling."
    : "The Kore Coaching flagship membership — two coached sessions weekly, unlimited classes, nutrition and lifestyle support. Start with the £99 30 Day Coached Trial.";

  return (
    <div>
      <Seo title={primaryTitle} description={primaryDesc} path={`/memberships/${slug}`} />

      {/* HEADER */}
      <section className="container-kore py-16 md:py-20 grid md:grid-cols-12 gap-14 items-start">
        <div className="md:col-span-6">
          <Link to="/memberships" className="text-xs text-kore-gold uppercase tracking-[0.2em]">← Memberships</Link>
          <h1 className="display-1 mt-6 text-kore-forest">{m.name}</h1>
          {m.tagline && <p className="mt-4 text-kore-gold uppercase tracking-[0.24em] text-xs">{m.tagline}</p>}

          {/* Pricing */}
          <div className="mt-10 space-y-4">
            {pricing.map((p, i) => (
              <div key={i} className={`rounded-2xl border p-5 flex items-baseline justify-between gap-4 ${i === 0 ? "border-kore-forest bg-kore-light/25" : "border-kore-gold/25 bg-white"}`}>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] text-kore-ink/60">{p.label}</div>
                  <div className="mt-1 flex items-baseline gap-1">
                    <span className="font-display text-4xl font-light text-kore-forest">£{p.price}</span>
                    <span className="text-sm text-kore-ink/55">{p.sub}</span>
                  </div>
                </div>
                {p.note && <span className={`text-[10px] uppercase tracking-[0.22em] px-3 py-1 rounded-full ${i === 0 ? "bg-kore-forest text-kore-warm" : "bg-kore-gold/15 text-kore-forest"}`}>{p.note}</span>}
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            {isAccess ? (
              <>
                <a
                  href={m.signup_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="membership-detail-cta"
                  onClick={() => trackCta({ cta_name: "membership_access_join", cta_text: "Join KORE Access", destination_url: m.signup_url, cta_location: "body" })}
                  className="btn-primary"
                >
                  Join KORE Access <ArrowUpRight size={16} />
                </a>
                <Link
                  to="/contact"
                  data-testid="membership-detail-enquire"
                  onClick={() => trackCta({ cta_name: "membership_access_enquire", cta_text: "Ask a question", destination_url: "/contact", cta_location: "body" })}
                  className="btn-ghost"
                >
                  Ask a question
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/30-day-trial"
                  data-testid="membership-detail-cta"
                  onClick={() => trackCta({ cta_name: "membership_coached_trial", cta_text: "Start your 30 Day Trial", destination_url: "/30-day-trial", cta_location: "body" })}
                  className="btn-primary"
                >
                  Start your 30 Day Trial <ArrowUpRight size={16} />
                </Link>
                <a
                  href={m.signup_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="membership-detail-monthly"
                  onClick={() => trackCta({ cta_name: "membership_coached_monthly", cta_text: "Skip the trial, join monthly", destination_url: m.signup_url, cta_location: "body" })}
                  className="btn-ghost"
                >
                  Skip the trial, join monthly
                </a>
              </>
            )}
          </div>
        </div>

        {/* What's included */}
        <div className="md:col-span-5 md:col-start-8 bg-white rounded-3xl p-10 shadow-soft border border-kore-gold/15">
          <h3 className="font-display text-2xl text-kore-forest">What's included</h3>
          <div className="rule-gold mt-3" />
          <ul className="mt-8 space-y-4">
            {m.features.map((f) => (
              <li key={f} className="flex gap-3 items-start"><Check size={16} className="text-kore-gold mt-1 shrink-0" /><span className="text-kore-ink/80">{f}</span></li>
            ))}
          </ul>
        </div>
      </section>

      {/* INTRO OFFER */}
      {offer && (
        <section data-testid={`intro-offer-${slug}`} className="bg-kore-forest text-kore-warm py-20 md:py-24">
          <div className="container-kore grid md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-kore-light">
                <Sparkles size={13} /> {offer.tag}
              </div>
              <h2 className="display-2 mt-4">{offer.heading}</h2>
              <p className="mt-5 text-kore-warm/80 leading-relaxed max-w-lg">{offer.body}</p>
            </div>
            <div className="md:col-span-4 md:col-start-9 flex md:justify-end">
              <Link
                to={offer.ctaTo}
                state={offer.stateNote ? { prefillMessage: offer.stateNote } : undefined}
                data-testid={`intro-offer-cta-${slug}`}
                className="btn-gold"
              >
                {offer.ctaLabel} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {slug === "kore-coached" && <KoreCoachedContent />}

      {/* Cross-sell / not sure */}
      <section className="container-kore py-20 md:py-24 text-center max-w-2xl">
        <span className="eyebrow">Still deciding?</span>
        <h2 className="display-2 mt-4 text-kore-forest">
          {isAccess ? "Want more structure and coaching?" : "Prefer to train independently?"}
        </h2>
        <p className="mt-5 text-kore-ink/70 leading-relaxed">
          {isAccess
            ? "Kore Coaching adds two coached sessions each week, a written programme, monthly check-ins and full accountability — for £149/month."
            : "Kore Access is our lighter option — calm, welcoming gym access from £39/month for people who already know how to train."}
        </p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Link to={isAccess ? "/memberships/kore-coached" : "/memberships/kore-access"} className="btn-primary">
            {isAccess ? "See Kore Coaching" : "See Kore Access"} <ArrowUpRight size={13} />
          </Link>
          <Link to="/help-me-choose" className="btn-ghost">Not sure? Help me choose</Link>
        </div>
      </section>
    </div>
  );
}
